/* eslint-disable no-irregular-whitespace */
import React, { useState, useEffect, useRef, useTransition, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Scan,
  Eraser,
  Copy,
  CheckCircle,
  X,
  FileText,
  Sparkles,
  AlertTriangle,
  UserPlus,
  Clock,
  Shield,
  TrendingUp,
  Crown,
  Infinity as InfinityIcon,
  Info,
} from "lucide-react";
import {
  detectWatermarks,
  removeWatermarks,
  terminateWorker,
} from "../utils/watermarkUtils";
import { useLocaleContext } from "../context/LocaleContext";
import { getTranslation } from "../utils/translations";
import { useSubscription } from "../hooks/useSubscription";
import { useAuth } from "./AuthProvider";
import PremiumUpgradeModal from "./PremiumUpgradeModal";
import PowerUserModal from "./PowerUserModal";
import { ChromeBadge } from "./ChromeBadge";
import type { User } from "@supabase/supabase-js";
import {
  trackScan,
  isLimitExceeded,
  getMinutesUntilReset,
  getScansUsed,
  getRemainingScans,
} from "../utils/scanLimits";
import {
  saveCooldownEndTime,
  initializeCooldown,
} from "../utils/cooldownStorage";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import { getLocalizedSlugSync } from "../blog/slugMapping";
import { getKnowledgeArticleUrlSync } from "../knowledge/slugMapping";
import {
  trackRybbitWatermarkDetection,
  trackRybbitWatermarkRemoval,
  trackRybbitTextCopy,
  trackRybbitExampleLoaded,
  trackRybbitCharacterLimitReached,
  trackRybbitScanPerformed,
  trackRybbitHourlyScanLimitReached,
  trackRybbitCooldownEncountered,
} from "../utils/rybbit";

// Lazy load markdown rendering to avoid bloating main bundle
// Only loaded when user activates preview mode
const MarkdownPreview = lazy(() => import("./MarkdownPreview"));

interface WatermarkRemoverProps {
  user: User | null;
}

const EXAMPLE_TEXT: Record<string, string> = {
  en: `This text looks normal, but invisible characters​ can hide between the words​ you see.`,
  de: `Dieser Text sieht normal aus, aber unsichtbare Zeichen​ können sich zwischen den Wörtern​ verstecken, die Sie sehen.`,
  'pt-BR': `Este texto parece normal, mas caracteres invisíveis​ podem se esconder entre as palavras​ que você vê.`,
  'ms-MY': `Teks ini kelihatan biasa, tetapi aksara tidak kelihatan​ boleh tersembunyi antara perkataan​ yang anda lihat.`,
  es: `Este texto parece normal, pero los caracteres invisibles​ pueden esconderse entre las palabras​ que ves.`,
  fr: `Ce texte semble normal, mais des caractères invisibles​ peuvent se cacher entre les mots​ que vous voyez.`,
  ja: `このテキストは一見普通ですが、見えない文字​が、見えている単語​の間に隠れていることがあります。`,
  id: `Teks ini terlihat normal, tetapi karakter tak terlihat​ bisa bersembunyi di antara kata-kata​ yang Anda lihat.`,
};

// Scanning progress step labels for each locale
const SCAN_STEPS: Record<string, { threshold: number; label: string }[]> = {
  en: [
    { threshold: 20, label: "Preparing text" },
    { threshold: 50, label: "Scanning characters" },
    { threshold: 80, label: "Identifying patterns" },
    { threshold: 100, label: "Finalizing results" },
  ],
  de: [
    { threshold: 20, label: "Text vorbereiten" },
    { threshold: 50, label: "Zeichen scannen" },
    { threshold: 80, label: "Muster identifizieren" },
    { threshold: 100, label: "Ergebnisse finalisieren" },
  ],
  'pt-BR': [
    { threshold: 20, label: "Preparando texto" },
    { threshold: 50, label: "Escaneando caracteres" },
    { threshold: 80, label: "Identificando padrões" },
    { threshold: 100, label: "Finalizando resultados" },
  ],
  'ms-MY': [
    { threshold: 20, label: "Menyediakan teks" },
    { threshold: 50, label: "Mengimbas aksara" },
    { threshold: 80, label: "Mengenal pasti corak" },
    { threshold: 100, label: "Memuktamadkan keputusan" },
  ],
  es: [
    { threshold: 20, label: "Preparando texto" },
    { threshold: 50, label: "Escaneando caracteres" },
    { threshold: 80, label: "Identificando patrones" },
    { threshold: 100, label: "Finalizando resultados" },
  ],
  fr: [
    { threshold: 20, label: "Préparation du texte" },
    { threshold: 50, label: "Analyse des caractères" },
    { threshold: 80, label: "Identification des motifs" },
    { threshold: 100, label: "Finalisation des résultats" },
  ],
  ja: [
    { threshold: 20, label: "テキストを準備中" },
    { threshold: 50, label: "文字をスキャン中" },
    { threshold: 80, label: "パターンを識別中" },
    { threshold: 100, label: "結果を確定中" },
  ],
  id: [
    { threshold: 20, label: "Menyiapkan teks" },
    { threshold: 50, label: "Memindai karakter" },
    { threshold: 80, label: "Mengidentifikasi pola" },
    { threshold: 100, label: "Menyelesaikan hasil" },
  ],
};

// UI labels that need locale support
const UI_LABELS: Record<string, {
  analyzingText: string;
  searchingWatermarks: string;
  progress: string;
  unlimited: string;
  unlimitedCharacters: string;
  scans: string;
  cooldownBetweenScans: string;
  signInForFeatures: string;
  signInWithGoogle: string;
  analyzing: string;
  removing: string;
  professionalDescription: string;
  dashes: string;
  keep: string;
  remove: string;
  replaceWithComma: string;
  customCharacter: string;
  customPlaceholder: string;
  detectFirst: string;
  cooldown: string;
}> = {
  en: {
    analyzingText: "Analyzing Text...",
    searchingWatermarks: "Searching for hidden watermarks",
    progress: "Progress",
    unlimited: "Unlimited",
    unlimitedCharacters: "Unlimited characters",
    scans: "scans",
    cooldownBetweenScans: "60-second cooldown between scans",
    signInForFeatures: "Sign in for enhanced features",
    signInWithGoogle: "Sign in with Google",
    analyzing: "Analyzing...",
    removing: "Removing...",
    professionalDescription: "Professional AI text detection and cleaning",
    dashes: "Dashes (— – ‒ ―)",
    keep: "Keep",
    remove: "Remove",
    replaceWithComma: "Replace with comma",
    customCharacter: "Custom character:",
    customPlaceholder: "e.g. ;",
    detectFirst: "Please run 'Detect Watermarks' first",
    cooldown: "Cooldown",
  },
  de: {
    analyzingText: "Analysiere Text...",
    searchingWatermarks: "Suche nach versteckten Wasserzeichen",
    progress: "Fortschritt",
    unlimited: "Unbegrenzt",
    unlimitedCharacters: "Unbegrenzte Zeichen",
    scans: "Scans",
    cooldownBetweenScans: "60-Sekunden-Wartezeit zwischen Scans",
    signInForFeatures: "Anmelden für erweiterte Features",
    signInWithGoogle: "Mit Google anmelden",
    analyzing: "Analysiere...",
    removing: "Entferne...",
    professionalDescription: "Professionelle KI-Texterkennung und -bereinigung",
    dashes: "Bindestriche (— – ‒ ―)",
    keep: "Behalten",
    remove: "Entfernen",
    replaceWithComma: "Mit Komma ersetzen",
    customCharacter: "Eigenes Zeichen:",
    customPlaceholder: "z.B. ;",
    detectFirst: "Bitte führen Sie zuerst 'Wasserzeichen erkennen' aus",
    cooldown: "Wartezeit",
  },
  'pt-BR': {
    analyzingText: "Analisando Texto...",
    searchingWatermarks: "Buscando marcas d'água ocultas",
    progress: "Progresso",
    unlimited: "Ilimitado",
    unlimitedCharacters: "Caracteres ilimitados",
    scans: "scans",
    cooldownBetweenScans: "60 segundos de espera entre scans",
    signInForFeatures: "Entre para recursos avançados",
    signInWithGoogle: "Entrar com Google",
    analyzing: "Analisando...",
    removing: "Removendo...",
    professionalDescription: "Detecção e limpeza profissional de texto IA",
    dashes: "Travessões (— – ‒ ―)",
    keep: "Manter",
    remove: "Remover",
    replaceWithComma: "Substituir por vírgula",
    customCharacter: "Caractere personalizado:",
    customPlaceholder: "ex. ;",
    detectFirst: "Execute 'Detectar Marcas d'água' primeiro",
    cooldown: "Espera",
  },
  'ms-MY': {
    analyzingText: "Menganalisis Teks...",
    searchingWatermarks: "Mencari tanda air tersembunyi",
    progress: "Kemajuan",
    unlimited: "Tanpa Had",
    unlimitedCharacters: "Aksara tanpa had",
    scans: "imbasan",
    cooldownBetweenScans: "60 saat menunggu antara imbasan",
    signInForFeatures: "Log masuk untuk ciri lanjutan",
    signInWithGoogle: "Log masuk dengan Google",
    analyzing: "Menganalisis...",
    removing: "Membuang...",
    professionalDescription: "Pengesanan dan pembersihan teks AI profesional",
    dashes: "Sempang (— – ‒ ―)",
    keep: "Kekal",
    remove: "Buang",
    replaceWithComma: "Ganti dengan koma",
    customCharacter: "Aksara tersuai:",
    customPlaceholder: "cth. ;",
    detectFirst: "Sila jalankan 'Kesan Tanda Air' dahulu",
    cooldown: "Menunggu",
  },
  es: {
    analyzingText: "Analizando Texto...",
    searchingWatermarks: "Buscando marcas de agua ocultas",
    progress: "Progreso",
    unlimited: "Ilimitado",
    unlimitedCharacters: "Caracteres ilimitados",
    scans: "escaneos",
    cooldownBetweenScans: "60 segundos de espera entre escaneos",
    signInForFeatures: "Inicia sesión para funciones avanzadas",
    signInWithGoogle: "Iniciar sesión con Google",
    analyzing: "Analizando...",
    removing: "Eliminando...",
    professionalDescription: "Detección y limpieza profesional de texto IA",
    dashes: "Guiones (— – ‒ ―)",
    keep: "Mantener",
    remove: "Eliminar",
    replaceWithComma: "Reemplazar con coma",
    customCharacter: "Carácter personalizado:",
    customPlaceholder: "ej. ;",
    detectFirst: "Ejecute 'Detectar Marcas de Agua' primero",
    cooldown: "Espera",
  },
  fr: {
    analyzingText: "Analyse du Texte...",
    searchingWatermarks: "Recherche de filigranes cachés",
    progress: "Progression",
    unlimited: "Illimité",
    unlimitedCharacters: "Caractères illimités",
    scans: "scans",
    cooldownBetweenScans: "60 secondes d'attente entre les scans",
    signInForFeatures: "Connectez-vous pour des fonctions avancées",
    signInWithGoogle: "Se connecter avec Google",
    analyzing: "Analyse...",
    removing: "Suppression...",
    professionalDescription: "Détection et nettoyage professionnel de texte IA",
    dashes: "Tirets (— – ‒ ―)",
    keep: "Garder",
    remove: "Supprimer",
    replaceWithComma: "Remplacer par une virgule",
    customCharacter: "Caractère personnalisé :",
    customPlaceholder: "ex. ;",
    detectFirst: "Veuillez d'abord exécuter 'Détecter les Filigranes'",
    cooldown: "Attente",
  },
  ja: {
    analyzingText: "テキストを分析中...",
    searchingWatermarks: "隠れた透かしを検索中",
    progress: "進行状況",
    unlimited: "無制限",
    unlimitedCharacters: "文字数無制限",
    scans: "スキャン",
    cooldownBetweenScans: "スキャン間に60秒の待機時間",
    signInForFeatures: "高度な機能にはログインしてください",
    signInWithGoogle: "Googleでログイン",
    analyzing: "分析中...",
    removing: "削除中...",
    professionalDescription: "プロフェッショナルなAIテキスト検出とクリーニング",
    dashes: "ダッシュ (— – ‒ ―)",
    keep: "維持",
    remove: "削除",
    replaceWithComma: "カンマで置換",
    customCharacter: "カスタム文字：",
    customPlaceholder: "例：;",
    detectFirst: "最初に「透かしを検出」を実行してください",
    cooldown: "待機中",
  },
  id: {
    analyzingText: "Menganalisis Teks...",
    searchingWatermarks: "Mencari watermark tersembunyi",
    progress: "Kemajuan",
    unlimited: "Tanpa Batas",
    unlimitedCharacters: "Karakter tanpa batas",
    scans: "pemindaian",
    cooldownBetweenScans: "60 detik tunggu antara pemindaian",
    signInForFeatures: "Masuk untuk fitur lanjutan",
    signInWithGoogle: "Masuk dengan Google",
    analyzing: "Menganalisis...",
    removing: "Menghapus...",
    professionalDescription: "Deteksi dan pembersihan teks AI profesional",
    dashes: "Tanda hubung (— – ‒ ―)",
    keep: "Pertahankan",
    remove: "Hapus",
    replaceWithComma: "Ganti dengan koma",
    customCharacter: "Karakter kustom:",
    customPlaceholder: "mis. ;",
    detectFirst: "Jalankan 'Deteksi Watermark' terlebih dahulu",
    cooldown: "Menunggu",
  },
};

const WatermarkRemover: React.FC<WatermarkRemoverProps> = ({ user }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scanDurationRef = useRef(0); // INP FIX: Store scan duration for CSS animation
  const stepTimersRef = useRef<NodeJS.Timeout[]>([]); // INP FIX: Track step timeouts for cleanup
  const [, startTransition] = useTransition(); // INP FIX: Low-priority scheduling for timer updates
  const [inputText, setInputText] = useState("");
  const [highlightedText, setHighlightedText] =
    useState<React.ReactNode | null>(null);
  const [cleanedText, setCleanedText] = useState("");
  const [watermarkCount, setWatermarkCount] = useState(0);
  const [isDetectionMode, setIsDetectionMode] = useState(false);
  const [isCleaned, setIsCleaned] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPowerUserModal, setShowPowerUserModal] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [currentAnalyzedText, setCurrentAnalyzedText] = useState("");
  const [wasPasted, setWasPasted] = useState(false);
  const [hasDetectedCurrent, setHasDetectedCurrent] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [dashHandlingMode, setDashHandlingMode] = useState<'none' | 'remove' | 'comma' | 'custom'>('none');
  const [customDashChar, setCustomDashChar] = useState('');
  const [scanProgress, setScanProgress] = useState(0); // INP FIX: Now only updates 4 times (at step thresholds) instead of every 50ms
  const [isScanning, setIsScanning] = useState(false);
  const { locale } = useLocaleContext();
  const {
    hasActiveSubscription,
    getSubscriptionType,
    loading: subscriptionLoading,
  } = useSubscription();

  // Use subscription status to determine limits and features
  const hasPremium = hasActiveSubscription();
  const subscriptionType = getSubscriptionType();

  // Set character limits based on subscription status
  const MAX_CHARS: number = hasPremium
    ? Number.POSITIVE_INFINITY
    : user
    ? 1500
    : 500;
  const COOLDOWN_SECONDS = 60;

  // CLS FIX: Removed animation setTimeout - was dead code (animateIn never used)

  // Initialize cooldown from localStorage on mount (only for non-signed-in users)
  useEffect(() => {
    if (!hasPremium && !user) {
      const remainingTime = initializeCooldown();
      if (remainingTime > 0) {
        setCooldownTime(remainingTime);
        console.log(
          "[Cooldown] Restored from localStorage:",
          remainingTime,
          "seconds"
        );
      }
    }
  }, [hasPremium, user]);

  // Cooldown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (cooldownTime > 0) {
      setIsOnCooldown(true);
      interval = setInterval(() => {
        // INP FIX: Mark timer tick as low-priority so user interactions take precedence
        startTransition(() => {
          setCooldownTime((prev) => {
            if (prev <= 1) {
              setIsOnCooldown(false);
              return 0;
            }
            return prev - 1;
          });
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [cooldownTime]);

  // Worker and timer cleanup on unmount
  useEffect(() => {
    return () => {
      terminateWorker();
      stepTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  // Debounced state sync mechanism - only updates React state after user stops typing
  const syncInputToState = useDebouncedCallback(() => {
    if (!textareaRef.current) return;

    let value = textareaRef.current.value;

    // Handle character limit for non-premium users
    if (!hasPremium && value.length > MAX_CHARS) {
      value = value.slice(0, MAX_CHARS);
      textareaRef.current.value = value;
      setShowLimitPopup(true);

      // Track character limit reached
      trackRybbitCharacterLimitReached({
        attempted_length: value.length,
        max_characters: MAX_CHARS,
        user_type: user ? "registered" : "anonymous",
      });
    }

    // Update all state in a single batch
    setInputText(value);
    setIsDetectionMode(false);
    setIsCleaned(false);
    setHighlightedText(null);
    setWasPasted(false);
    setHasDetectedCurrent(false);
  }, 300);

  // Lightweight input change handler - no synchronous state updates
  const handleInputChange = () => {
    syncInputToState();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (!textareaRef.current) return;

    let pastedText = e.clipboardData.getData("text");

    // Handle character limit for non-premium users
    if (!hasPremium && pastedText.length > MAX_CHARS) {
      pastedText = pastedText.slice(0, MAX_CHARS);
      setShowLimitPopup(true);

      // Track character limit reached
      trackRybbitCharacterLimitReached({
        attempted_length: pastedText.length,
        max_characters: MAX_CHARS,
        user_type: user ? "registered" : "anonymous",
      });
    }

    // Update textarea value directly (uncontrolled)
    textareaRef.current.value = pastedText;

    // Update state immediately for paste (better UX for paste operation)
    setInputText(pastedText);
    setIsDetectionMode(false);
    setIsCleaned(false);
    setHighlightedText(null);
    setWasPasted(true); // Mark that text was pasted
    setHasDetectedCurrent(false);
  };

  const handleShowExample = () => {
    if (!textareaRef.current) return;

    let exampleText = EXAMPLE_TEXT[locale] || EXAMPLE_TEXT.en;

    // Handle character limit for non-premium users
    if (!hasPremium && exampleText.length > MAX_CHARS) {
      exampleText = exampleText.slice(0, MAX_CHARS);
      setShowLimitPopup(true);
    }

    // Update textarea value directly (uncontrolled)
    textareaRef.current.value = exampleText;

    // Update state immediately for example load (better UX)
    setInputText(exampleText);
    setIsDetectionMode(false);
    setIsCleaned(false);
    setHighlightedText(null);
    setIsPreviewMode(false);
    setWasPasted(false); // Example text is not pasted
    setHasDetectedCurrent(false);

    // Track example loaded event to Rybbit
    trackRybbitExampleLoaded({
      locale: locale,
      user_type: hasPremium ? "premium" : "free",
    });
  };

  const handleDetect = async () => {
    // Allow detection for all users, but apply cooldown for non-premium users
    if (!inputText.trim() || isDetecting || isScanning) return;

    // Check if user is signed in but doesn't have premium
    if (user && !hasPremium) {
      setShowPremiumModal(true);
      return;
    }

    // Check hourly scan limit for non-signed-in users FIRST (before cooldown)
    // Block if already at 0 scans, but allow the 5th scan to complete
    if (!hasPremium && !user) {
      if (isLimitExceeded()) {
        // Already at 0 scans - block the scan
        setShowPowerUserModal(true);
        return;
      }
    }

    // Only check cooldown for non-premium AND non-signed-in users
    if (!hasPremium && !user && isOnCooldown) {
      // Track cooldown encountered
      trackRybbitCooldownEncountered({
        cooldown_remaining_seconds: cooldownTime,
      });
      return;
    }

    // Calculate scan duration based on character count (1-3 seconds)
    // More characters = longer scan time, but never more than 3 seconds
    const charCount = inputText.length;
    const minDuration = 1000; // 1 second minimum
    const maxDuration = 3000; // 3 seconds maximum
    // Scale duration: 0 chars = 1s, 500+ chars = 3s (linear interpolation)
    const scanDuration = Math.min(
      maxDuration,
      minDuration + (charCount / 500) * (maxDuration - minDuration)
    );

    // Start scanning animation
    // INP FIX: Progress bar now uses CSS animation instead of 50ms setInterval.
    // Only 4 discrete state updates (at step thresholds) instead of 20-60 per scan.
    scanDurationRef.current = scanDuration;
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = [];
    setIsScanning(true);
    setScanProgress(0);
    setIsDetecting(true);

    // Schedule 4 discrete updates at step thresholds (20%, 50%, 80%) during scan
    // The 100% step is set after the worker returns, not here
    const steps = SCAN_STEPS[locale] || SCAN_STEPS.en;
    steps.forEach((step) => {
      if (step.threshold >= 100) return; // 100% is handled after worker completes
      const delay = (step.threshold / 90) * scanDuration;
      stepTimersRef.current.push(
        setTimeout(() => setScanProgress(step.threshold), delay)
      );
    });

    // Wait for the scan animation duration
    await new Promise((resolve) => setTimeout(resolve, scanDuration));

    try {
      const requiresNormalization = !hasPremium && !user && wasPasted;

      // ASYNC: All heavy work now happens in Web Worker
      const { highlightedOutput, count, modifiedText } = await detectWatermarks(
        inputText,
        hasPremium,
        requiresNormalization
      );

      // Complete the progress to 100%
      stepTimersRef.current.forEach(clearTimeout);
      stepTimersRef.current = [];
      setScanProgress(100);

      // Small delay to show 100% before showing results
      await new Promise((resolve) => setTimeout(resolve, 200));

      setHighlightedText(highlightedOutput);
      setWatermarkCount(count);
      setIsDetectionMode(true);
      setIsCleaned(false);
      setHasDetectedCurrent(true); // Mark that detection has been run for current text

      if (modifiedText) {
        setCurrentAnalyzedText(modifiedText);
      } else {
        setCurrentAnalyzedText(inputText);
      }

      // Track watermark detection event to Rybbit
      trackRybbitWatermarkDetection({
        text_length: inputText.length,
        watermarks_found: count,
        user_type: hasPremium ? "premium" : "free",
        subscription_type: subscriptionType,
      });

      // Track the scan in localStorage for non-signed-in users
      if (!hasPremium && !user) {
        const remainingBefore = getRemainingScans();
        trackScan();
        const remainingAfter = getRemainingScans();
        const scansUsedNow = getScansUsed();

        // Track scan performed with remaining count
        trackRybbitScanPerformed({
          scans_remaining: remainingAfter,
          scans_used: scansUsedNow,
        });

        // Show popup if this was the last scan (went from 1 to 0)
        if (remainingBefore === 1) {
          // Track hourly limit reached
          trackRybbitHourlyScanLimitReached({
            scans_used: 5,
            time_until_reset_minutes: getMinutesUntilReset(),
          });

          // Use setTimeout to show modal after detection results are displayed
          setTimeout(() => {
            setShowPowerUserModal(true);
          }, 500);
        }
      }

      // Start cooldown only for non-premium users and save to localStorage
      if (!hasPremium && !user) {
        setCooldownTime(COOLDOWN_SECONDS);
        saveCooldownEndTime(COOLDOWN_SECONDS);
      }
    } catch (error) {
      console.error("[WatermarkDetection] Error during detection:", error);
      stepTimersRef.current.forEach(clearTimeout);
      stepTimersRef.current = [];
    } finally {
      setIsDetecting(false);
      setIsScanning(false);
      setScanProgress(0);
    }
  };

  const handleRemove = async () => {
    if (!inputText.trim() || isRemoving) return;

    try {
      setIsRemoving(true);

      const textToClean = currentAnalyzedText || inputText;

      // Build dash handling options
      const dashHandling = dashHandlingMode !== 'none'
        ? { mode: dashHandlingMode, customChar: customDashChar }
        : undefined;

      // ASYNC: Cleaning now happens in Web Worker
      const cleaned = await removeWatermarks(textToClean, dashHandling);

      setCleanedText(cleaned);
      setHighlightedText(null);
      setIsDetectionMode(false);
      setIsCleaned(true);

      // Track watermark removal event to Rybbit
      trackRybbitWatermarkRemoval({
        text_length: textToClean.length,
        user_type: hasPremium ? "premium" : "free",
        subscription_type: subscriptionType ?? undefined,
        dash_handling_mode: dashHandlingMode,
      });

      // Note: handleRemove does NOT trigger cooldown or scan tracking
      // Only handleDetect tracks scans since that's the rate-limited action
    } catch (error) {
      console.error("[WatermarkRemoval] Error during removal:", error);
      // Optionally show error to user
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCopy = async () => {
    const textToCopy = isCleaned ? cleanedText : inputText;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      // Track text copy event to Rybbit
      trackRybbitTextCopy({
        text_length: textToCopy.length,
        was_cleaned: isCleaned,
        user_type: hasPremium ? "premium" : "free",
      });
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleSignUpFromPopup = () => {
    setShowLimitPopup(false);

    navigate("/auth");
  };

  const charsLeft: number = hasPremium
    ? Number.POSITIVE_INFINITY
    : MAX_CHARS - inputText.length;
  const isDetectDisabled =
    !inputText.trim() || (!hasPremium && !user && isOnCooldown) || isDetecting || isScanning;

  // REMOVED: Conditional loading render that caused hydration mismatch (React Error #418)
  // The component now always renders the same structure on both server and client
  // Loading states are handled within the component UI instead of replacing it entirely

  return (
    <div>
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="watermark_detection"
      />

      {showLimitPopup && (
        <div className="fixed inset-0 bg-neutral-950/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 p-6 sm:p-8 rounded-2xl shadow-xl max-w-md w-full border border-neutral-200 dark:border-neutral-800 animate-scale-in">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {getTranslation(locale, "watermarkRemover.limitPopup.title")}
                </h3>
              </div>
              <button
                onClick={() => setShowLimitPopup(false)}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                aria-label="Close popup"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-5 leading-relaxed">
              {getTranslation(locale, "watermarkRemover.limitPopup.message", {
                max: MAX_CHARS,
              })}
            </p>

            {!hasPremium && (
              <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 mb-5 border border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-4 h-4 text-accent-500" />
                  <span className="font-medium text-sm text-neutral-900 dark:text-white">
                    {getTranslation(
                      locale,
                      "watermarkRemover.limitPopup.upgradeTitle"
                    )}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 text-sm text-neutral-600 dark:text-neutral-400">
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    <span>{(UI_LABELS[locale] || UI_LABELS.en).unlimitedCharacters}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-neutral-600 dark:text-neutral-400">
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    <span>{getTranslation(locale, "watermarkRemover.limitPopup.feature2")}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-neutral-600 dark:text-neutral-400">
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    <span>{getTranslation(locale, "watermarkRemover.limitPopup.feature3")}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {!hasPremium && (
                <button
                  onClick={handleSignUpFromPopup}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <UserPlus size={16} />
                  {getTranslation(locale, "watermarkRemover.limitPopup.signUpButton")}
                </button>
              )}
              <button
                onClick={() => setShowLimitPopup(false)}
                className="flex-1 px-4 py-2.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors font-medium text-sm"
              >
                {getTranslation(locale, "watermarkRemover.limitPopup.continueButton")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanning Animation Overlay */}
      {isScanning && (
        <div className="fixed inset-0 bg-neutral-950/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 p-6 sm:p-8 rounded-2xl shadow-xl max-w-sm w-full border border-neutral-200 dark:border-neutral-800 animate-scale-in">
            <div className="flex flex-col items-center mb-6">
              <div className="relative mb-5">
                <div className="w-16 h-16 rounded-2xl bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center border border-accent-100 dark:border-accent-800">
                  <Scan className="w-7 h-7 text-accent-600 dark:text-accent-400" />
                </div>
                <div className="absolute -inset-1 rounded-2xl border-2 border-accent-500/30 animate-ping" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                {(UI_LABELS[locale] || UI_LABELS.en).analyzingText}
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400 text-center text-sm">
                {(UI_LABELS[locale] || UI_LABELS.en).searchingWatermarks}
              </p>
            </div>

            <div className="mb-5">
              <div className="flex justify-end text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                <span>{(UI_LABELS[locale] || UI_LABELS.en).progress}</span>
              </div>
              <div className="h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-accent-500 rounded-full ${scanProgress >= 100 ? '' : 'animate-scan-progress'}`}
                  style={scanProgress >= 100
                    ? { width: '100%', transition: 'width 200ms ease-out' }
                    : { animationDuration: `${scanDurationRef.current}ms` }
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              {(SCAN_STEPS[locale] || SCAN_STEPS.en).map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2.5 text-sm transition-colors ${
                    scanProgress >= step.threshold
                      ? "text-green-600 dark:text-green-400"
                      : "text-neutral-300 dark:text-neutral-600"
                  }`}
                >
                  <CheckCircle size={14} className={scanProgress >= step.threshold ? "" : "opacity-40"} />
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div id="remover" className="mt-12 md:mt-16">
        {/* Main Tool Card */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden">
          {/* Header Section */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-accent-50 dark:bg-accent-900/20 rounded-xl border border-accent-100 dark:border-accent-800">
                  <Shield className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getTranslation(locale, "watermarkRemover.title")}
                  </h2>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                    {(UI_LABELS[locale] || UI_LABELS.en).professionalDescription}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleShowExample}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-700 transition-colors"
                >
                  <FileText size={15} />
                  <span>{getTranslation(locale, "watermarkRemover.showExample")}</span>
                </button>

                <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {getTranslation(locale, "watermarkRemover.edit")}
                  </span>
                  <button
                    onClick={() => setIsPreviewMode(!isPreviewMode)}
                    aria-label={isPreviewMode ? "Switch to edit mode" : "Switch to preview mode"}
                    aria-checked={isPreviewMode}
                    role="switch"
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      isPreviewMode ? "bg-accent-600" : "bg-neutral-400 dark:bg-neutral-500"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                        isPreviewMode ? "translate-x-4" : ""
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {getTranslation(locale, "watermarkRemover.preview")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Text Input Area */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6">
            <div
              className="relative"
              onClick={() => isPreviewMode && setIsPreviewMode(false)}
            >
              {isPreviewMode ? (
                <div className="min-h-[280px] p-5 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-auto prose prose-neutral dark:prose-invert prose-sm max-w-none cursor-pointer hover:border-accent-300 dark:hover:border-accent-600 transition-colors">
                  <Suspense
                    fallback={
                      // CLS FIX: Skeleton with matching min-height to prevent layout shift
                      <div className="min-h-[240px] animate-pulse">
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4 mb-3" />
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full mb-3" />
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-5/6 mb-3" />
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-2/3" />
                      </div>
                    }
                  >
                    <MarkdownPreview
                      content={
                        inputText ||
                        getTranslation(locale, "watermarkRemover.noContent")
                      }
                    />
                  </Suspense>
                </div>
              ) : (
                <textarea
                  id="text-input"
                  ref={textareaRef}
                  defaultValue={inputText}
                  onChange={handleInputChange}
                  onPaste={handlePaste}
                  placeholder={getTranslation(
                    locale,
                    "watermarkRemover.placeholder"
                  )}
                  className="w-full min-h-[280px] p-5 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 dark:focus:border-accent-500 text-neutral-900 dark:text-white transition-[box-shadow,border-color] resize-y font-mono text-sm leading-relaxed placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
                />
              )}
            </div>

            {/* Status Bar - CLS FIX: min-height reserves space for auth-conditional badges */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 min-h-[42px]">
              <div className="flex flex-wrap items-center gap-2">
                {/* Character Count */}
                <div
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                    hasPremium
                      ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                      : charsLeft < 100
                      ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                      : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700"
                  }`}
                >
                  {hasPremium && <InfinityIcon size={12} />}
                  {hasPremium
                    ? (UI_LABELS[locale] || UI_LABELS.en).unlimited
                    : `${charsLeft.toLocaleString()} / ${MAX_CHARS.toLocaleString()}`}
                </div>

                {/* Scan Counter */}
                {!hasPremium && !user && (
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      getRemainingScans() <= 1
                        ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                        : getRemainingScans() <= 2
                        ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                        : "bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400 border border-accent-200 dark:border-accent-800"
                    }`}
                  >
                    <Scan size={12} />
                    {getRemainingScans()}/5 {(UI_LABELS[locale] || UI_LABELS.en).scans}
                  </div>
                )}

                {/* Premium Badge */}
                {hasPremium && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400 rounded-lg text-xs font-medium border border-accent-200 dark:border-accent-800">
                    {subscriptionType === "lifetime" ? <Crown size={12} /> : <TrendingUp size={12} />}
                    {subscriptionType === "lifetime" ? "Lifetime" : "Premium"}
                  </div>
                )}

                {/* Cooldown */}
                {isOnCooldown && !hasPremium && !user && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-xs font-medium border border-amber-200 dark:border-amber-800">
                    <Clock size={12} className="animate-pulse" />
                    {cooldownTime}s
                  </div>
                )}
              </div>

              <ChromeBadge />
            </div>
          </div>

          {/* Options Section */}
          <div className="px-5 sm:px-6 lg:px-8 py-4 border-t border-neutral-100 dark:border-neutral-800">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {(UI_LABELS[locale] || UI_LABELS.en).dashes}
              </span>
              <div className="flex flex-wrap items-center gap-4">
                {/* Keep dashes */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={dashHandlingMode === 'none'}
                    onClick={() => setDashHandlingMode('none')}
                    className={`relative w-4 h-4 rounded-full border-2 transition-colors flex items-center justify-center ${
                      dashHandlingMode === 'none'
                        ? "border-accent-600"
                        : "border-neutral-300 dark:border-neutral-600 group-hover:border-accent-400"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full bg-accent-600 transition-opacity ${
                        dashHandlingMode === 'none' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 select-none">
                    {(UI_LABELS[locale] || UI_LABELS.en).keep}
                  </span>
                </label>

                {/* Remove dashes */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={dashHandlingMode === 'remove'}
                    onClick={() => setDashHandlingMode('remove')}
                    className={`relative w-4 h-4 rounded-full border-2 transition-colors flex items-center justify-center ${
                      dashHandlingMode === 'remove'
                        ? "border-accent-600"
                        : "border-neutral-300 dark:border-neutral-600 group-hover:border-accent-400"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full bg-accent-600 transition-opacity ${
                        dashHandlingMode === 'remove' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 select-none">
                    {(UI_LABELS[locale] || UI_LABELS.en).remove}
                  </span>
                </label>

                {/* Replace with comma */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={dashHandlingMode === 'comma'}
                    onClick={() => setDashHandlingMode('comma')}
                    className={`relative w-4 h-4 rounded-full border-2 transition-colors flex items-center justify-center ${
                      dashHandlingMode === 'comma'
                        ? "border-accent-600"
                        : "border-neutral-300 dark:border-neutral-600 group-hover:border-accent-400"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full bg-accent-600 transition-opacity ${
                        dashHandlingMode === 'comma' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 select-none">
                    {(UI_LABELS[locale] || UI_LABELS.en).replaceWithComma}
                  </span>
                </label>

                {/* Custom character */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={dashHandlingMode === 'custom'}
                    onClick={() => setDashHandlingMode('custom')}
                    className={`relative w-4 h-4 rounded-full border-2 transition-colors flex items-center justify-center ${
                      dashHandlingMode === 'custom'
                        ? "border-accent-600"
                        : "border-neutral-300 dark:border-neutral-600 group-hover:border-accent-400"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full bg-accent-600 transition-opacity ${
                        dashHandlingMode === 'custom' ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 select-none">
                    {(UI_LABELS[locale] || UI_LABELS.en).customCharacter}
                  </span>
                  <input
                    type="text"
                    value={customDashChar}
                    onChange={(e) => {
                      setCustomDashChar(e.target.value);
                      if (e.target.value) {
                        setDashHandlingMode('custom');
                      }
                    }}
                    onClick={() => setDashHandlingMode('custom')}
                    placeholder={(UI_LABELS[locale] || UI_LABELS.en).customPlaceholder}
                    maxLength={5}
                    className="w-16 px-2 py-1 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 dark:focus:border-accent-500 text-neutral-900 dark:text-white transition-colors"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 bg-neutral-50 dark:bg-neutral-800/30 border-t border-neutral-100 dark:border-neutral-800">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDetect}
                disabled={isDetectDisabled}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-600 hover:bg-accent-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 text-white disabled:text-neutral-500 dark:disabled:text-neutral-400 rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
                title={
                  isOnCooldown && !hasPremium && !user
                    ? `${(UI_LABELS[locale] || UI_LABELS.en).cooldown}: ${cooldownTime}s`
                    : ""
                }
              >
                {isDetecting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isOnCooldown && !hasPremium && !user ? (
                  <Clock size={18} />
                ) : (
                  <Scan size={18} />
                )}
                <span>
                  {isDetecting
                    ? (UI_LABELS[locale] || UI_LABELS.en).analyzing
                    : isOnCooldown && !hasPremium && !user
                    ? `${getTranslation(locale, "watermarkRemover.detectButton")} (${cooldownTime}s)`
                    : getTranslation(locale, "watermarkRemover.detectButton")}
                </span>
              </button>

              <button
                onClick={handleRemove}
                disabled={!inputText.trim() || !hasDetectedCurrent || isRemoving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 text-white disabled:text-neutral-500 dark:disabled:text-neutral-400 rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
                title={
                  !hasDetectedCurrent && inputText.trim()
                    ? (UI_LABELS[locale] || UI_LABELS.en).detectFirst
                    : ""
                }
              >
                {isRemoving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Eraser size={18} />
                )}
                <span>
                  {isRemoving
                    ? (UI_LABELS[locale] || UI_LABELS.en).removing
                    : getTranslation(locale, "watermarkRemover.removeButton")}
                </span>
              </button>

              <button
                onClick={handleCopy}
                disabled={!inputText.trim() && !cleanedText}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:bg-neutral-100 dark:disabled:bg-neutral-800 text-neutral-700 dark:text-neutral-300 disabled:text-neutral-400 dark:disabled:text-neutral-500 border border-neutral-200 dark:border-neutral-700 rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
              >
                {copySuccess ? (
                  <CheckCircle size={18} className="text-green-500" />
                ) : (
                  <Copy size={18} />
                )}
                <span>
                  {copySuccess
                    ? getTranslation(locale, "watermarkRemover.copied")
                    : getTranslation(locale, "watermarkRemover.copyButton")}
                </span>
              </button>
            </div>

            {/* Sign-in prompt for anonymous users - CLS FIX: min-height reserves space */}
            <div className="min-h-[88px]">
              {!hasPremium && !user && (
                <div className="mt-5 p-4 bg-white dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg shrink-0">
                        <Clock className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {(UI_LABELS[locale] || UI_LABELS.en).cooldownBetweenScans}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                          {(UI_LABELS[locale] || UI_LABELS.en).signInForFeatures}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={signIn}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600 text-neutral-900 dark:text-white border border-neutral-200 dark:border-neutral-600 rounded-lg font-medium text-sm transition-colors shrink-0"
                    >
                      <img src="/google-icon.svg" alt="Google" width="16" height="16" className="w-4 h-4" />
                      <span>
                        {(UI_LABELS[locale] || UI_LABELS.en).signInWithGoogle}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Detection Results */}
        {isDetectionMode && (
          <div className="mt-6 bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden animate-slide-up">
            <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-neutral-100 dark:border-neutral-800">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-accent-50 dark:bg-accent-900/20 rounded-xl border border-accent-100 dark:border-accent-800">
                  <Scan className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getTranslation(locale, "watermarkRemover.detectionResults")}
                  </h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {getTranslation(locale, "watermarkRemover.foundWatermarks", {
                      count: watermarkCount,
                      s: watermarkCount !== 1 ? "s" : "",
                    })}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6">
              <div className="max-h-[400px] overflow-y-auto bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-5 border border-neutral-200 dark:border-neutral-700 prose prose-neutral dark:prose-invert prose-sm max-w-none">
                {highlightedText}
              </div>
            </div>
          </div>
        )}

        {/* Cleaned Text Results */}
        {isCleaned && (
          <div className="mt-6 bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden animate-slide-up">
            <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-green-100 dark:border-green-900/30 bg-green-50/50 dark:bg-green-900/10">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-green-100 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-800">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getTranslation(locale, "watermarkRemover.cleanedText")}
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {getTranslation(locale, "watermarkRemover.watermarksRemoved")}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6">
              <div className="max-h-[400px] overflow-y-auto bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-5 border border-neutral-200 dark:border-neutral-700 prose prose-neutral dark:prose-invert prose-sm max-w-none">
                <Suspense
                  fallback={
                    // CLS FIX: Skeleton with matching height to prevent layout shift
                    <div className="min-h-[100px] animate-pulse">
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4 mb-3" />
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full mb-3" />
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-5/6" />
                    </div>
                  }
                >
                  <MarkdownPreview content={cleanedText} />
                </Suspense>
              </div>

              {/* FAQ: Still seeing watermarks */}
              <div className="mt-4 p-5 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-base text-neutral-900 dark:text-white mb-1.5">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.stillSeeing")}
                    </p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.stillSeeingAnswer")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Educational Notice */}
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-neutral-700 dark:text-neutral-300">
                    <p className="font-medium text-neutral-900 dark:text-white mb-2">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.title")}
                    </p>
                    <ul className="space-y-1.5 text-neutral-600 dark:text-neutral-400 list-disc list-inside">
                      <li>{getTranslation(locale, "watermarkRemover.educationalNotice.removed")}</li>
                      <li>{getTranslation(locale, "watermarkRemover.educationalNotice.formatting")}</li>
                    </ul>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Link
                        to={`${locale === 'en' ? '' : locale === 'pt-BR' ? '/pt-br' : locale === 'ms-MY' ? '/my' : `/${locale}`}/blog/${getLocalizedSlugSync('why-ai-detector-saying-writing-is-ai', locale)}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors underline underline-offset-2"
                      >
                        {getTranslation(locale, "watermarkRemover.educationalNotice.learnMore")}
                      </Link>
                      <Link
                        to={getKnowledgeArticleUrlSync('watermark-removal-vs-ai-detection', locale)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors underline underline-offset-2"
                      >
                        {getTranslation(locale, "watermarkRemover.educationalNotice.removalVsDetection")}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Power User Modal - Hourly Scan Limit */}
      <PowerUserModal
        isOpen={showPowerUserModal}
        onClose={() => setShowPowerUserModal(false)}
        onSignup={() => {
          setShowPowerUserModal(false);
          signIn();
        }}
      />
    </div>
  );
};

export default WatermarkRemover;
